import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  alpha,
  styled,
  TablePagination,
  Stack,
  FormControlLabel,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ru";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import {
  useGetAllSportObjectsQuery,
  useGetAllDictionariesQuery,
  TechnicalCondition,
} from "graphql/graphQlApiHooks";
import { ImportedSportEquipmentRecord } from "./types";
import { rowValidate } from "./utils";

interface EditableTableProps {
  data: ImportedSportEquipmentRecord[];
  onDataChange: (data: ImportedSportEquipmentRecord[]) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  submitting?: boolean;
}

type RowStatus = "valid" | "error" | "warning";
type FilterStatus = "all" | "valid" | "warning" | "error";

// Стили в стиле Google Sheets 2025
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.background.paper,
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.grey[100],
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.grey[500],
    },
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: 1700,
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "hasError" && prop !== "hasNotExactMatch" && prop !== "hasNotFound",
})<{ status?: RowStatus; hasError?: boolean; hasNotExactMatch?: boolean; hasNotFound?: boolean }>(
  ({ theme, status, hasError, hasNotExactMatch, hasNotFound }) => {
    // Розовый - не заполнены обязательные поля (error)
    const errorColor = '#ffb3ba'; // Розовый цвет
    // Голубой - не точное совпадение (info)
    const infoColor = theme.palette.info.light || '#74CAFF'; // Голубой цвет
    // Оранжевый - не найдено в базе (warning)
    const warningColor = '#ffcc99'; // Оранжевый/персиковый цвет
    
    return {
      padding: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      position: "relative",
      height: 40,
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.action.hover, 0.04),
      },
      // Розовый - не заполнены обязательные поля (приоритет 1)
      ...(hasError && {
        backgroundColor: alpha(errorColor, 0.3),
        "&:hover": {
          backgroundColor: alpha(errorColor, 0.4),
        },
      }),
      // Оранжевый - не найдено в базе (приоритет 2)
      ...(hasNotFound && !hasError && {
        backgroundColor: alpha(warningColor, 0.3),
        "&:hover": {
          backgroundColor: alpha(warningColor, 0.4),
        },
      }),
      // Голубой - не точное совпадение (приоритет 3)
      ...(hasNotExactMatch && !hasError && !hasNotFound && {
        backgroundColor: alpha(infoColor, 0.3),
        "&:hover": {
          backgroundColor: alpha(infoColor, 0.4),
        },
      }),
      // Фон строки (менее яркий, если ячейка не имеет собственной ошибки)
      ...(status === "error" && !hasError && {
        backgroundColor: alpha(theme.palette.error.main, 0.04),
        "&:hover": {
          backgroundColor: alpha(theme.palette.error.main, 0.08),
        },
      }),
      ...(status === "warning" && !hasNotExactMatch && !hasError && !hasNotFound && {
        backgroundColor: alpha(theme.palette.warning.main, 0.04),
        "&:hover": {
          backgroundColor: alpha(theme.palette.warning.main, 0.08),
        },
      }),
    };
  }
);

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "selected",
})<{ status?: RowStatus; selected?: boolean }>(
  ({ theme, status, selected }) => ({
    "&:hover": {
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.action.hover, 0.02),
    },
    ...(status === "error" && {
      borderLeft: `3px solid ${theme.palette.error.main}`,
      backgroundColor: alpha(theme.palette.error.main, 0.04),
    }),
    ...(status === "warning" && {
      borderLeft: `3px solid ${theme.palette.warning.main}`,
      backgroundColor: alpha(theme.palette.warning.main, 0.04),
    }),
    ...(status === "valid" && {
      borderLeft: `3px solid ${theme.palette.success.main}`,
      backgroundColor: alpha(theme.palette.success.main, 0.02),
    }),
    ...(selected && {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    }),
  })
);

const HeaderCell = styled(TableCell)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.grey[50],
  fontWeight: 600,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: theme.palette.text.secondary,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 1),
  whiteSpace: "nowrap",
  height: 36,
}));

const EditableCell = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  minHeight: 28,
  fontSize: "0.813rem",
  padding: 0,
  "& .MuiTextField-root": {
    width: "100%",
    height: "100%",
    "& .MuiInputBase-root": {
      fontSize: "0.813rem",
      padding: 0,
      margin: 0,
      height: "100%",
      border: "none",
      background: "transparent",
      "&:before": {
        display: "none",
      },
      "&:after": {
        display: "none",
      },
      "&:hover:not(.Mui-disabled):before": {
        display: "none",
      },
      "& fieldset": {
        display: "none",
      },
      "&.Mui-focused": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
        borderRadius: 2,
      },
    },
    "& .MuiInputBase-input": {
      padding: theme.spacing(0.75, 1),
      margin: 0,
      height: "100%",
    },
    "& textarea": {
      padding: theme.spacing(0.75, 1),
      margin: 0,
      height: "100% !important",
      resize: "none",
    },
    "& .MuiFormHelperText-root": {
      display: "none",
    },
  },
  "& .MuiAutocomplete-root": {
    width: "100%",
    height: "100%",
    "& .MuiInputBase-root": {
      padding: 0,
      margin: 0,
      height: "100%",
      fontSize: "0.813rem",
      background: "transparent",
      "&:before": {
        display: "none",
      },
      "&:after": {
        display: "none",
      },
      "& fieldset": {
        display: "none",
      },
      "&.Mui-focused": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
        borderRadius: 2,
      },
    },
    "& .MuiInputBase-input": {
      padding: theme.spacing(0.75, 1),
      margin: 0,
      height: "100%",
    },
    "& .MuiAutocomplete-endAdornment": {
      display: "none",
    },
  },
  "& .MuiDatePicker-root": {
    width: "100%",
    height: "100%",
    "& .MuiInputBase-root": {
      padding: 0,
      margin: 0,
      height: "100%",
      "&.Mui-focused": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
        borderRadius: 2,
      },
      "& .MuiInputBase-input": {
        padding: theme.spacing(0.75, 1),
        margin: 0,
        height: "100%",
      },
    },
  },
}));

// Компонент для ячейки с DatePicker
const DatePickerCellComponent: React.FC<{
  record: ImportedSportEquipmentRecord;
  rowIndex: number;
  status: RowStatus;
  errors: any;
  hasError: boolean;
  hasNotExactMatch: boolean;
  hasNotFound: boolean;
  handleKeyDown: (e: React.KeyboardEvent, rowId: string, field: string, rowIndex: number) => void;
  updateRecord: (rowId: string, updates: Partial<ImportedSportEquipmentRecord>) => void;
  focusedField: string | null;
  setFocusedField: (field: string | null) => void;
}> = ({ record, rowIndex, status, errors, hasError, hasNotExactMatch, hasNotFound, handleKeyDown, updateRecord, focusedField, setFocusedField }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <StyledTableCell
      status={status}
      hasError={hasError}
      hasNotExactMatch={hasNotExactMatch}
      hasNotFound={hasNotFound}
      data-cell-id={`${record.id}-commissioningDate`}
      sx={{ cursor: "text" }}
    >
      <EditableCell>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
          <DatePicker
            open={pickerOpen}
            
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            inputFormat="DD.MM.YYYY"
            views={["day", "month", "year"]}
            value={
              record.commissioningDate && String(record.commissioningDate).trim()
                ? (() => {
                    const date = dayjs(String(record.commissioningDate).trim(), "YYYY-MM-DD");
                    return date.isValid() ? date : null;
                  })()
                : null
            }
            onChange={(newValue: Dayjs | null) => {
              if (newValue && newValue.isValid()) {
                updateRecord(record.id, {
                  commissioningDate: newValue.format("YYYY-MM-DD"),
                  isCommissioningDateCorrect: true,
                });
              } else {
                updateRecord(record.id, {
                  commissioningDate: undefined,
                  isCommissioningDateCorrect: false,
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={!!errors.commissioningDate}
                inputProps={{
                  ...params.inputProps,
                  placeholder: "дд.мм.гггг",
                }}
                onFocus={() => setFocusedField(`${record.id}-commissioningDate`)}
                onBlur={() => setFocusedField(null)}
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    e.preventDefault();
                    handleKeyDown(e, record.id, "commissioningDate", rowIndex);
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
        {errors.commissioningDate && focusedField !== `${record.id}-commissioningDate` && (
          <Tooltip title={errors.commissioningDate.message}>
            <ErrorIcon
              sx={{
                fontSize: 16,
                color: "error.main",
                ml: 0.5,
                position: "absolute",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
          </Tooltip>
        )}
      </EditableCell>
    </StyledTableCell>
  );
};

const EditableTable: React.FC<EditableTableProps> = ({
  data,
  onDataChange,
  onSubmit,
  disabled = false,
  submitting = false,
}) => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: sportObjectsData } = useGetAllSportObjectsQuery();
  const { data: dictionariesData } = useGetAllDictionariesQuery();

  const sportObjects = useMemo(
    () => sportObjectsData?.getAllSportObjects || [],
    [sportObjectsData]
  );

  const dictionaries = useMemo(() => {
    const response = dictionariesData?.getAllSportKindDictionary;
    if (response && "__typename" in response && response.__typename === "GetAllSportKindsResponse") {
      return {
        sportCategories: response.sportCategories || [],
        sportKinds: response.sportKinds || [],
        equipmentTypes: response.equipmentTypes || [],
      };
    }
    return { sportCategories: [], sportKinds: [], equipmentTypes: [] };
  }, [dictionariesData]);

  // Определение статуса строки
  const getRowStatus = (record: ImportedSportEquipmentRecord): RowStatus => {
    const errors = rowValidate(record);
    if (Object.keys(errors).length > 1) return "error";
    
    const warningFlags = {
      isSportObjectEqual: record.isSportObjectEqual,
      isSportZoneEqual: record.isSportZoneEqual,
      isSportSubZoneEqual: record.isSportSubZoneEqual,
      isSportCategoryEqual: record.isSportCategoryEqual,
      isSportKindEqual: record.isSportKindEqual,
      isEquipmentTypeEqual: record.isEquipmentTypeEqual,
      isTechnicalConditionEqual: record.isTechnicalConditionEqual,
      isInUseEqual: record.isInUseEqual,
      isCommissioningDateCorrect: record.commissioningDate ? record.isCommissioningDateCorrect : true,
    };
    
    const hasWarnings =
      (record.isSportObjectEqual === false) ||
      (record.isSportZoneEqual === false) ||
      (record.isSportSubZoneEqual === false) ||
      (record.isSportCategoryEqual === false) ||
      (record.isSportKindEqual === false) ||
      (record.isEquipmentTypeEqual === false) ||
      (record.isTechnicalConditionEqual === false) ||
      (record.isInUseEqual === false) ||
      (record.commissioningDate && record.isCommissioningDateCorrect === false);
    
    if (hasWarnings) {
      const falseFlags = Object.entries(warningFlags)
        .filter(([_, value]) => value === false)
        .map(([key]) => key);
    }
    
    return hasWarnings ? "warning" : "valid";
  };

  // Функция для определения ошибок/предупреждений в конкретном поле
  // Возвращает тип статуса: 'error' (розовый - не заполнены обязательные поля), 
  // 'warning' (голубой - не точное совпадение), 'notFound' (оранжевый - не найдено в базе)
  const getFieldStatus = (record: ImportedSportEquipmentRecord, field: string) => {
    const errors = rowValidate(record);
    const hasError = !!errors[field]; // Розовый - не заполнены обязательные поля
    
    // Проверка не точного совпадения (голубой)
    let hasNotExactMatch = false;
    // Проверка не найдено в базе (оранжевый) - когда поле должно быть заполнено, но значение не найдено
    let hasNotFound = false;
    
    switch (field) {
      case "sportObject":
        hasNotExactMatch = record.isSportObjectEqual === false;
        hasNotFound = !record.sportObject && !!errors.sportObject; // Если требуется, но не выбрано
        break;
      case "sportZone":
        hasNotExactMatch = record.isSportZoneEqual === false;
        // Если объект выбран, но зона не выбрана (зона необязательна, но может быть не найдена)
        hasNotFound = !record.sportZone && !!record.sportObject;
        break;
      case "sportSubZone":
        hasNotExactMatch = record.isSportSubZoneEqual === false;
        // Если зона выбрана, но подзона не выбрана (подзона необязательна, но может быть не найдена)
        hasNotFound = !record.sportSubZone && !!record.sportZone;
        break;
      case "sportCategory":
        hasNotExactMatch = record.isSportCategoryEqual === false;
        hasNotFound = !record.sportCategory && !!errors.sportCategory;
        break;
      case "sportKind":
        hasNotExactMatch = record.isSportKindEqual === false;
        hasNotFound = !record.sportKind && !!errors.sportKind;
        break;
      case "equipmentType":
        hasNotExactMatch = record.isEquipmentTypeEqual === false;
        hasNotFound = !record.equipmentType && !!errors.equipmentType;
        break;
      case "technicalCondition":
        hasNotExactMatch = record.isTechnicalConditionEqual === false;
        break;
      case "inUse":
        hasNotExactMatch = record.isInUseEqual === false;
        break;
      case "commissioningDate":
        hasNotExactMatch = record.commissioningDate ? record.isCommissioningDateCorrect === false : false;
        hasNotFound = !record.commissioningDate && !!errors.commissioningDate;
        break;
    }
    
    return { hasError, hasNotExactMatch, hasNotFound };
  };

  // Фильтрация данных
  const filteredData = useMemo(() => {
    if (filterStatus === "all") {
      return data;
    }
    return data.filter((record) => getRowStatus(record) === filterStatus);
  }, [data, filterStatus, getRowStatus]);

  // Сбрасываем страницу при изменении фильтра или если текущая страница выходит за пределы отфильтрованных данных
  useEffect(() => {
    if (page > 0 && filteredData.length <= page * rowsPerPage) {
      setPage(0);
    }
  }, [filteredData.length, page, rowsPerPage]);

  // Пагинация отфильтрованных данных
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page, rowsPerPage]);

  // Обработчики пагинации
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Статистика
  const stats = useMemo(() => {
    const valid = data.filter((r) => getRowStatus(r) === "valid").length;
    const warning = data.filter((r) => getRowStatus(r) === "warning").length;
    const error = data.filter((r) => getRowStatus(r) === "error").length;
    // Готовые к импорту = valid + warning (можно импортировать даже с предупреждениями)
    const readyToImport = valid + warning;
    return { total: data.length, valid, warning, error, readyToImport };
  }, [data]);

  // Обновление записи
  const updateRecord = (rowId: string, updates: Partial<ImportedSportEquipmentRecord>) => {
    const newData = data.map((record) => {
      if (record.id !== rowId) return record;
      
      const updated = { ...record, ...updates };
      
      // Каскадные обновления - только если поле явно изменено (есть в updates)
      if ('sportObject' in updates && (updates.sportObject === null || updates.sportObject === undefined)) {
        updated.sportZone = undefined;
        updated.sportSubZone = undefined;
        updated.isSportZoneEqual = undefined;
        updated.isSportSubZoneEqual = undefined;
      }
      if ('sportZone' in updates && (updates.sportZone === null || updates.sportZone === undefined)) {
        updated.sportSubZone = undefined;
        updated.isSportSubZoneEqual = undefined;
      }
      
      // Устанавливаем флаги is*Equal в true при выборе значений из справочников
      if ('sportObject' in updates && updates.sportObject) {
        updated.isSportObjectEqual = true;
      }
      if ('sportZone' in updates && updates.sportZone) {
        updated.isSportZoneEqual = true;
      }
      if ('sportSubZone' in updates && updates.sportSubZone) {
        updated.isSportSubZoneEqual = true;
      }
      if ('sportCategory' in updates && updates.sportCategory) {
        updated.isSportCategoryEqual = true;
      }
      if ('sportKind' in updates && updates.sportKind) {
        updated.isSportKindEqual = true;
      }
      if ('equipmentType' in updates && updates.equipmentType) {
        updated.isEquipmentTypeEqual = true;
      }
      if ('technicalCondition' in updates && updates.technicalCondition !== undefined) {
        updated.isTechnicalConditionEqual = true;
      }
      if ('inUse' in updates && updates.inUse !== undefined) {
        updated.isInUseEqual = true;
      }
      if ('commissioningDate' in updates && updates.commissioningDate) {
        updated.isCommissioningDateCorrect = true;
      }
      
      return updated;
    });
    onDataChange(newData);
  };

  // Навигация Tab между ячейками
  const handleKeyDown = (e: React.KeyboardEvent, rowId: string, field: string, rowIndex: number) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const currentFieldIndex = columns.findIndex((col) => col.id === field);
      const isShiftPressed = e.shiftKey;
      
      let nextFieldIndex: number;
      let nextRowIndex: number;
      
      if (!isShiftPressed) {
        // Навигация вперед
        if (currentFieldIndex < columns.length - 1) {
          nextFieldIndex = currentFieldIndex + 1;
          nextRowIndex = rowIndex;
        } else if (rowIndex < filteredData.length - 1) {
          nextFieldIndex = 1; // Пропускаем колонку выбора
          nextRowIndex = rowIndex + 1;
        } else {
          return; // Последняя ячейка таблицы
        }
      } else {
        // Навигация назад
        if (currentFieldIndex > 1) {
          nextFieldIndex = currentFieldIndex - 1;
          nextRowIndex = rowIndex;
        } else if (rowIndex > 0) {
          nextFieldIndex = columns.length - 1;
          nextRowIndex = rowIndex - 1;
        } else {
          return; // Первая ячейка таблицы
        }
      }
      
      // Используем filteredData для навигации, но учитываем пагинацию
      const nextRow = filteredData[nextRowIndex];
      if (!nextRow) return; // Если строка не найдена, выходим
      const nextField = columns[nextFieldIndex].id;
      
      // Проверяем, находится ли следующая строка на текущей странице
      const nextRowPage = Math.floor(nextRowIndex / rowsPerPage);
      if (nextRowPage !== page) {
        // Если следующая строка на другой странице, переключаем страницу
        setPage(nextRowPage);
        // Фокусируемся после переключения страницы
        setTimeout(() => {
          const nextCell = document.querySelector(`[data-cell-id="${nextRow.id}-${nextField}"]`) as HTMLElement;
          if (nextCell) {
            let focusableElement = nextCell.querySelector('input:not([type="hidden"])') as HTMLElement;
            if (!focusableElement) {
              focusableElement = nextCell.querySelector('textarea') as HTMLElement;
            }
            if (!focusableElement) {
              focusableElement = nextCell.querySelector('[tabindex="0"]') as HTMLElement;
            }
            if (focusableElement) {
              focusableElement.focus();
            } else {
              nextCell.click();
            }
          }
        }, 100);
        return;
      }
      
      setTimeout(() => {
        const nextCell = document.querySelector(`[data-cell-id="${nextRow.id}-${nextField}"]`) as HTMLElement;
        if (nextCell) {
          // Пытаемся найти input или другой фокусируемый элемент
          // Сначала ищем input напрямую
          let focusableElement = nextCell.querySelector('input:not([type="hidden"])') as HTMLElement;
          
          // Если не нашли, ищем textarea
          if (!focusableElement) {
            focusableElement = nextCell.querySelector('textarea') as HTMLElement;
          }
          
          // Если не нашли, ищем элемент с tabindex="0"
          if (!focusableElement) {
            focusableElement = nextCell.querySelector('[tabindex="0"]') as HTMLElement;
          }
          
          // Если нашли фокусируемый элемент, фокусируем его
          if (focusableElement) {
            focusableElement.focus();
            // Для Autocomplete может потребоваться дополнительная обработка
            if (focusableElement.tagName === 'INPUT' && focusableElement.closest('.MuiAutocomplete-root')) {
              // Даем время Autocomplete обработать фокус
              setTimeout(() => {
                focusableElement?.focus();
              }, 10);
            }
          } else {
            // Для чекбоксов и других элементов
            nextCell.click();
          }
        }
      }, 0);
    }
  };

  // Обработка выбора строки
  const handleRowSelect = (rowId: string, checked: boolean) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      checked ? newSet.add(rowId) : newSet.delete(rowId);
      return newSet;
    });
  };

  // Обработка удаления
  const handleDelete = () => {
    onDataChange(data.filter((record) => !selectedRows.has(record.id)));
    setSelectedRows(new Set());
    setDeleteDialogOpen(false);
  };

  // Получение зависимых данных
  const getZonesForObject = (objectId?: string) => {
    if (!objectId) {
      return [];
    }
    const zones = (sportObjects.find((o) => o.id === objectId)?.sportZones || []) as any[];
    return zones;
  };

  const getSubZonesForZone = (zoneId?: string) => {
    if (!zoneId) {
      return [];
    }
    const subZones = (sportObjects.flatMap((o) => o.sportZones || []).find((z) => z.id === zoneId)?.sportSubZones || []) as any[];
    return subZones;
  };

  const getKindsForCategory = (categoryId?: string) => {
    if (!categoryId) return dictionaries.sportKinds;
    return dictionaries.sportKinds.filter((k) => k.sportCategoryId === categoryId) as any[];
  };

  const getTypesForKind = (kindId?: string) => {
    if (!kindId) return dictionaries.equipmentTypes;
    return dictionaries.equipmentTypes.filter((t) => t.sportKindId === kindId) as any[];
  };

  const columns = [
    { id: "select", label: "", width: 40 },
    { id: "name", label: "Название оборудования", width: 200 },
    { id: "accountingName", label: "Бух. название", width: 180 },
    { id: "description", label: "Описание", width: 180 },
    { id: "inventoryNumber", label: "Инв. номер", width: 140 },
    { id: "technicalCondition", label: "Тех. состояние", width: 140 },
    { id: "failureReason", label: "Причина неисправности", width: 180 },
    { id: "inUse", label: "Используется", width: 120 },
    { id: "commissioningDate", label: "Дата ввода", width: 120 },
    { id: "sportObject", label: "Спортивный объект", width: 160 },
    { id: "sportZone", label: "Здание/Помещение", width: 150 },
    { id: "sportSubZone", label: "Спортивная зона", width: 160 },
    { id: "sportCategory", label: "Категория", width: 140 },
    { id: "sportKind", label: "Вид спорта", width: 140 },
    { id: "equipmentType", label: "Тип оборудования", width: 160 },
    { id: "length", label: "Длина (см)", width: 100 },
    { id: "width", label: "Ширина (см)", width: 100 },
    { id: "height", label: "Высота (см)", width: 100 },
    { id: "weight", label: "Вес (кг)", width: 100 },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Панель фильтров и статистики */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={(_, value) => value && setFilterStatus(value)}
          size="small"
        >
          <ToggleButton value="all">Все ({stats.total})</ToggleButton>
          <ToggleButton value="valid">
            <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5, color: "success.main" }} />
            Готово ({stats.valid})
          </ToggleButton>
          <ToggleButton value="warning">
            <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: "warning.main" }} />
            Предупреждения ({stats.warning})
          </ToggleButton>
          <ToggleButton value="error">
            <ErrorIcon sx={{ fontSize: 16, mr: 0.5, color: "error.main" }} />
            Ошибки ({stats.error})
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={selectedRows.size === 0 || submitting}
            size="small"
          >
            Удалить ({selectedRows.size})
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={disabled || submitting || stats.readyToImport === 0}
            size="small"
          >
            {submitting ? "Импорт..." : `Импортировать (${stats.readyToImport})`}
          </Button>
        </Box>
      </Box>

      {/* Таблица */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <StyledTableContainer sx={{ flex: 1 }}>
        <StyledTable stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <HeaderCell key={column.id} sx={{ width: column.width, minWidth: column.width }}>
                  {column.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((record, rowIndex) => {
              const status = getRowStatus(record);
              const isSelected = selectedRows.has(record.id);
              const errors = rowValidate(record);
              const actualRowIndex = page * rowsPerPage + rowIndex;

              return (
                <StyledTableRow key={record.id} status={status} selected={isSelected}>
                  {/* Чекбокс выбора */}
                  <StyledTableCell
                    status={status}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowSelect(record.id, !isSelected);
                    }}
                    sx={{ cursor: "pointer" }}
                    data-cell-id={`${record.id}-select`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowSelect(record.id, !isSelected);
                      } else {
                        handleKeyDown(e, record.id, "select", actualRowIndex);
                      }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRowSelect(record.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </StyledTableCell>

                  {/* Название */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.name}
                    data-cell-id={`${record.id}-name`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={record.name}
                        onChange={(e) => updateRecord(record.id, { name: e.target.value })}
                        onFocus={() => setFocusedField(`${record.id}-name`)}
                        onBlur={() => setFocusedField(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "name", actualRowIndex);
                          }
                        }}
                        error={!!errors.name}
                        placeholder={errors.name ? errors.name.message : ""}
                      />
                      {errors.name && focusedField !== `${record.id}-name` && (
                        <Tooltip title={errors.name.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Бухгалтерское название */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-accountingName`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={record.accountingName || ""}
                        onChange={(e) => updateRecord(record.id, { accountingName: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "accountingName", actualRowIndex);
                          }
                        }}
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Описание */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-description`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        value={record.description || ""}
                        onChange={(e) => updateRecord(record.id, { description: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" && !e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "description", actualRowIndex);
                          }
                        }}
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Инвентарный номер */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.inventoryNumber}
                    data-cell-id={`${record.id}-inventoryNumber`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={record.inventoryNumber}
                        onChange={(e) => updateRecord(record.id, { inventoryNumber: e.target.value })}
                        onFocus={() => setFocusedField(`${record.id}-inventoryNumber`)}
                        onBlur={() => setFocusedField(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "inventoryNumber", actualRowIndex);
                          }
                        }}
                        error={!!errors.inventoryNumber}
                        placeholder={errors.inventoryNumber ? errors.inventoryNumber.message : ""}
                      />
                      {errors.inventoryNumber && focusedField !== `${record.id}-inventoryNumber` && (
                        <Tooltip title={errors.inventoryNumber.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Техническое состояние */}
                  <StyledTableCell
                    status={status}
                    hasNotExactMatch={getFieldStatus(record, "technicalCondition").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "technicalCondition").hasNotFound}
                    data-cell-id={`${record.id}-technicalCondition`}
                    sx={{ cursor: "default" }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const isWorking = record.technicalCondition === TechnicalCondition.Working;
                        const nextCondition = isWorking ? TechnicalCondition.NotWorking : TechnicalCondition.Working;
                        updateRecord(record.id, { technicalCondition: nextCondition });
                      } else {
                        handleKeyDown(e, record.id, "technicalCondition", actualRowIndex);
                      }
                    }}
                  >
                    <EditableCell>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          px: 1,
                          py: 0.5,
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                          },
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={record.technicalCondition === TechnicalCondition.Working}
                            onChange={(e) => {
                              const nextCondition = e.target.checked
                                ? TechnicalCondition.Working
                                : TechnicalCondition.NotWorking;
                              updateRecord(record.id, { technicalCondition: nextCondition });
                            }}
                          />
                        }
                        label="Исправно"
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Причина неисправности */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.failureReason}
                    data-cell-id={`${record.id}-failureReason`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        value={record.failureReason || ""}
                        onChange={(e) => updateRecord(record.id, { failureReason: e.target.value })}
                        onFocus={() => setFocusedField(`${record.id}-failureReason`)}
                        onBlur={() => setFocusedField(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" && !e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "failureReason", actualRowIndex);
                          }
                        }}
                        error={!!errors.failureReason}
                        placeholder={errors.failureReason ? errors.failureReason.message : ""}
                      />
                      {errors.failureReason && focusedField !== `${record.id}-failureReason` && (
                        <Tooltip title={errors.failureReason.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Используется */}
                  <StyledTableCell
                    status={status}
                    hasNotExactMatch={getFieldStatus(record, "inUse").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "inUse").hasNotFound}
                    sx={{ cursor: "default" }}
                    data-cell-id={`${record.id}-inUse`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        updateRecord(record.id, { inUse: !record.inUse });
                      } else {
                        handleKeyDown(e, record.id, "inUse", actualRowIndex);
                      }
                    }}
                  >
                    <EditableCell>
                      <FormControlLabel
                        sx={{
                          m: 0,
                          px: 1,
                          py: 0.5,
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                          },
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={!!record.inUse}
                            onChange={(e) => {
                              updateRecord(record.id, { inUse: e.target.checked });
                            }}
                          />
                        }
                        label="Используется"
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Дата ввода в эксплуатацию */}
                  <DatePickerCellComponent
                    record={record}
                    rowIndex={actualRowIndex}
                    status={status}
                    errors={errors}
                    hasError={!!errors.commissioningDate}
                    hasNotExactMatch={getFieldStatus(record, "commissioningDate").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "commissioningDate").hasNotFound}
                    handleKeyDown={handleKeyDown}
                    updateRecord={updateRecord}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />

                  {/* Спортивный объект */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.sportObject}
                    hasNotExactMatch={getFieldStatus(record, "sportObject").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "sportObject").hasNotFound}
                    data-cell-id={`${record.id}-sportObject`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <Autocomplete
                        openOnFocus
                        options={sportObjects as any[]}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.sportObject as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { sportObject: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "sportObject", actualRowIndex);
                          }
                        }}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Выберите объект"
                            error={!!errors.sportObject}
                            onFocus={() => setFocusedField(`${record.id}-sportObject`)}
                            onBlur={() => setFocusedField(null)}
                          />
                        )}
                      />
                      {errors.sportObject && focusedField !== `${record.id}-sportObject` && (
                        <Tooltip title={errors.sportObject.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Спортивная зона */}
                  <StyledTableCell
                    status={status}
                    hasNotExactMatch={getFieldStatus(record, "sportZone").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "sportZone").hasNotFound}
                    data-cell-id={`${record.id}-sportZone`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      {(() => {
                        return null;
                      })()}
                      <Autocomplete
                        openOnFocus
                        options={getZonesForObject(record.sportObject?.id)}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.sportZone as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { sportZone: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "sportZone", actualRowIndex);
                          }
                        }}
                        disabled={!record.sportObject}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={record.sportObject ? "Выберите зону" : "Сначала выберите объект"}
                          />
                        )}
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Спортивная подзона */}
                  <StyledTableCell
                    status={status}
                    hasNotExactMatch={getFieldStatus(record, "sportSubZone").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "sportSubZone").hasNotFound}
                    data-cell-id={`${record.id}-sportSubZone`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      {(() => {
                        return null;
                      })()}
                      <Autocomplete
                        openOnFocus
                        options={getSubZonesForZone(record.sportZone?.id)}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.sportSubZone as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { sportSubZone: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "sportSubZone", actualRowIndex);
                          }
                        }}
                        disabled={!record.sportZone}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={record.sportZone ? "Выберите подзону" : "Сначала выберите зону"}
                          />
                        )}
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Категория */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.sportCategory}
                    hasNotExactMatch={getFieldStatus(record, "sportCategory").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "sportCategory").hasNotFound}
                    data-cell-id={`${record.id}-sportCategory`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <Autocomplete
                        openOnFocus
                        options={dictionaries.sportCategories as any[]}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.sportCategory as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { sportCategory: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "sportCategory", actualRowIndex);
                          }
                        }}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Выберите категорию"
                            error={!!errors.sportCategory}
                            onFocus={() => setFocusedField(`${record.id}-sportCategory`)}
                            onBlur={() => setFocusedField(null)}
                          />
                        )}
                      />
                      {errors.sportCategory && focusedField !== `${record.id}-sportCategory` && (
                        <Tooltip title={errors.sportCategory.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Вид спорта */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.sportKind}
                    hasNotExactMatch={getFieldStatus(record, "sportKind").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "sportKind").hasNotFound}
                    data-cell-id={`${record.id}-sportKind`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <Autocomplete
                        openOnFocus
                        options={getKindsForCategory(record.sportCategory?.id)}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.sportKind as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { sportKind: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "sportKind", actualRowIndex);
                          }
                        }}
                        disabled={!record.sportCategory}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={
                              record.sportCategory ? "Выберите вид спорта" : "Сначала выберите категорию"
                            }
                            error={!!errors.sportKind}
                            onFocus={() => setFocusedField(`${record.id}-sportKind`)}
                            onBlur={() => setFocusedField(null)}
                          />
                        )}
                      />
                      {errors.sportKind && focusedField !== `${record.id}-sportKind` && (
                        <Tooltip title={errors.sportKind.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Тип оборудования */}
                  <StyledTableCell
                    status={status}
                    hasError={!!errors.equipmentType}
                    hasNotExactMatch={getFieldStatus(record, "equipmentType").hasNotExactMatch}
                    hasNotFound={getFieldStatus(record, "equipmentType").hasNotFound}
                    data-cell-id={`${record.id}-equipmentType`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <Autocomplete
                        openOnFocus
                        options={getTypesForKind(record.sportKind?.id)}
                        getOptionLabel={(option: any) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={(record.equipmentType as any) || null}
                        onChange={(_, newValue) => {
                          updateRecord(record.id, { equipmentType: newValue || undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "equipmentType", actualRowIndex);
                          }
                        }}
                        disabled={!record.sportKind}
                        componentsProps={{
                          paper: {
                            sx: {
                              minWidth: 300,
                              maxWidth: 500,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={
                              record.sportKind ? "Выберите тип" : "Сначала выберите вид спорта"
                            }
                            error={!!errors.equipmentType}
                            onFocus={() => setFocusedField(`${record.id}-equipmentType`)}
                            onBlur={() => setFocusedField(null)}
                          />
                        )}
                      />
                      {errors.equipmentType && focusedField !== `${record.id}-equipmentType` && (
                        <Tooltip title={errors.equipmentType.message}>
                          <ErrorIcon
                            sx={{
                              fontSize: 16,
                              color: "error.main",
                              ml: 0.5,
                              position: "absolute",
                              right: 4,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </EditableCell>
                  </StyledTableCell>

                  {/* Длина */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-length`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={record.length ?? ""}
                        onChange={(e) => {
                          const value = e.target.value === "" ? null : parseFloat(e.target.value);
                          updateRecord(record.id, { length: value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "length", actualRowIndex);
                          }
                        }}
                        placeholder="см"
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Ширина */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-width`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={record.width ?? ""}
                        onChange={(e) => {
                          const value = e.target.value === "" ? null : parseFloat(e.target.value);
                          updateRecord(record.id, { width: value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "width", actualRowIndex);
                          }
                        }}
                        placeholder="см"
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Высота */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-height`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={record.height ?? ""}
                        onChange={(e) => {
                          const value = e.target.value === "" ? null : parseFloat(e.target.value);
                          updateRecord(record.id, { height: value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "height", actualRowIndex);
                          }
                        }}
                        placeholder="см"
                      />
                    </EditableCell>
                  </StyledTableCell>

                  {/* Вес */}
                  <StyledTableCell
                    status={status}
                    data-cell-id={`${record.id}-weight`}
                    sx={{ cursor: "text" }}
                  >
                    <EditableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={record.weight ?? ""}
                        onChange={(e) => {
                          const value = e.target.value === "" ? null : parseFloat(e.target.value);
                          updateRecord(record.id, { weight: value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Tab") {
                            e.preventDefault();
                            handleKeyDown(e, record.id, "weight", actualRowIndex);
                          }
                        }}
                        placeholder="кг"
                      />
                    </EditableCell>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </StyledTable>
        </StyledTableContainer>

        {/* Футер с легендой и пагинацией */}
        {filteredData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: (theme) => theme.palette.background.paper,
              px: 2,
              py: 0.5,
              minHeight: 52,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Легенда слева */}
            <Stack 
              direction="row" 
              spacing={1.5} 
              alignItems="center" 
              flexWrap="wrap" 
              sx={{ 
                flex: 1,
                minWidth: 0, // Позволяет сжиматься
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: '#ffb3ba',
                    flexShrink: 0,
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "text.secondary", 
                    fontSize: "0.7rem", 
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  заполнены не все обязательные поля
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: '#74CAFF',
                    flexShrink: 0,
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "text.secondary", 
                    fontSize: "0.7rem", 
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  не точное совпадение
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: '#ffcc99',
                    flexShrink: 0,
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "text.secondary", 
                    fontSize: "0.7rem", 
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  не удалось найти значение в базе
                </Typography>
              </Stack>
            </Stack>

            {/* Пагинация справа */}
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Строк на странице:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`
              }
              sx={{
                borderTop: "none",
                flexShrink: 0,
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  paddingRight: 0,
                  minHeight: 40,
                },
                "& .MuiTablePagination-spacer": {
                  display: "none",
                },
                "& .MuiTablePagination-selectLabel": {
                  margin: 0,
                },
                "& .MuiTablePagination-displayedRows": {
                  margin: 0,
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить {selectedRows.size}{" "}
            {selectedRows.size === 1 ? "запись" : "записей"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditableTable;
